// The ADP funding split. Pure integer-cent arithmetic: contribution =
// min(cap, round(cost * 3/4)); the client pays the balance. cost*3 is an exact
// integer and /4 is exactly representable in float, so no dollar ever drifts.
//
// Interpretation note (asymmetric binaural): the cap applies to the FITTING,
// not per ear — a binaural fitting is 75% of the two aids' combined cost capped
// at $1000, which diverges from a per-ear $500 cap only when the two aids are
// priced unequally. This follows the manual's plain "$1000 for binaural"
// language; verify against a real dual-price ADP invoice before Phase 1 (see
// research/adp-mechanics-2026-07-06.md open items).

import { ADP_CAPS, type AdpFunding, type Cents, type DeviceAid, type FittingType } from "./types";

export function totalCost(aids: DeviceAid[]): Cents {
  return aids.reduce((sum, a) => sum + a.costCents, 0);
}

export function seventyFive(costCents: Cents): Cents {
  return Math.round((costCents * 3) / 4);
}

export function computeFunding(fittingType: FittingType, aids: DeviceAid[]): AdpFunding {
  const totalCostCents = totalCost(aids);
  const cap = ADP_CAPS[fittingType];
  const rawSeventyFiveCents = seventyFive(totalCostCents);
  const contributionCents = Math.min(cap, rawSeventyFiveCents);
  return {
    fittingType,
    totalCostCents,
    cap,
    rawSeventyFiveCents,
    contributionCents,
    clientPaysCents: totalCostCents - contributionCents,
    cappedOut: rawSeventyFiveCents > cap,
  };
}
